<template>
  <SteppedModal
    v-model="isOpen"
    data-testid="onboarding-modal"
    :steps-count="stepsValues.length"
    :current-step="currentStepIndex + 1"
    :title="stepTitle"
    :description="stepDescription"
    @close="closeOnboarding"
  >
    <component
      :is="currentStepComponent"
      :finished-onboarding="refreshToken"
      :show-back-button="firstStep !== currentStep"
      @error="stoppedSaving"
      @success="stoppedSaving"
      @prev="prevStep"
      @next="nextStep"
      @closeModal="closeModal"
      @saving="savingStep"
      @stoppedSaving="stoppedSaving"
    />

    <!-- We skip the loader during the DEMOGRAPHIC step to avoid blocking the UI -->

    <LoadingOverlay
      v-if="($apollo.loading && steps.DEMOGRAPHIC !== currentStep) || saving"
      class="xl:tw-rounded-3xl"
      absolute
    />
  </SteppedModal>
</template>

<script>
import { mapState } from 'vuex'
import fp from 'lodash/fp'
import { gql } from 'graphql-tag'
import SteppedModal from './SteppedModal'
import ProfilePictureUploadStep from '~/components/onboarding/ProfilePictureUploadStep'
import DemographicStep from '~/components/onboarding/DemographicStep'
import AddressStep from '~/components/onboarding/AddressStep'
import SecurityStep from '~/components/onboarding/SecurityStep'
import EmailInputStep from '~/components/onboarding/EmailInputStep'
import PhoneInputStep from '~/components/onboarding/PhoneInputStep'
import HouseholdMembersStep from '~/components/onboarding/HouseholdMembersStep'
import GiveBackStep from '~/components/onboarding/GiveBackStep'
import CongratulationsStep from '~/components/onboarding/CongratulationsStep'
import LoadingOverlay from '@/components/shared/LoadingOverlay'

const steps = {
  DEMOGRAPHIC: 'DEMOGRAPHIC',
  ADDRESS: 'ADDRESS',
  UPLOAD_PROFILE_PICTURE: 'UPLOAD_PROFILE_PICTURE',
  SECURITY: 'SECURITY',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  HOUSEHOLD_MEMBERS: 'HOUSEHOLD_MEMBERS',
  GIVE_BACK: 'GIVE_BACK',
  CONGRATULATIONS: 'CONGRATULATIONS',
}

export default {
  components: {
    HouseholdMembersStep,
    GiveBackStep,
    CongratulationsStep,
    PhoneInputStep,
    EmailInputStep,
    ProfilePictureUploadStep,
    DemographicStep,
    AddressStep,
    SteppedModal,
    SecurityStep,
    LoadingOverlay,
  },
  apollo: {
    auths: {
      query: gql`
        query entityAuthClaims {
          me {
            entityAuthClaims {
              authType
              authValue
              status
            }
          }
        }
      `,
      update: fp.prop('me.entityAuthClaims'),
      result(res, key) {
        if (!this.auths) return

        const emails = this.auths.filter((x) => x.authType === 'email')
        const phones = this.auths.filter((x) => x.authType === 'phone')
        if (emails.length > 0) {
          this.stepsValues = this.stepsValues.filter(
            (item) => item !== steps.EMAIL
          )
        }
        if (phones.length > 0) {
          this.stepsValues = this.stepsValues.filter(
            (item) => item !== steps.PHONE
          )
        }
      },
    },
    addresses: {
      query: gql`
        query addresses {
          me {
            addresses {
              addressUuid
            }
          }
        }
      `,
      update: fp.prop('me.addresses'),
      result(res, key) {
        if (this.addresses.length > 0) {
          this.stepsValues = this.stepsValues.filter(
            (item) => item !== steps.ADDRESS
          )

          // If the current step is ADDRESS, we move to the next one
          if (this.currentStep === steps.ADDRESS) {
            this.currentStep = this.stepsValues[this.currentStepIndex + 1]
          }
        }
      },
    },
  },
  data() {
    return {
      steps,
      stepsMeta: {
        DEMOGRAPHIC: {
          component: 'DemographicStep',
          title: this.$t('onboarding_demographic_title'),
          description: this.$t('onboarding_demographic_description'),
        },
        ADDRESS: {
          component: 'AddressStep',
          title: this.$t('onboarding_demographic_title'),
          description: this.$t('onboarding_address_description'),
        },
        UPLOAD_PROFILE_PICTURE: {
          component: 'ProfilePictureUploadStep',
          title: this.$t('onboarding_demographic_title'),
          description: this.$t('onboarding_profile_picture_description'),
        },
        SECURITY: {
          component: 'SecurityStep',
          title: this.$t('onboarding_security_title'),
          description: this.$t('onboarding_security_description'),
        },
        EMAIL: {
          component: 'EmailInputStep',
          title: this.$t('onboarding_email_title'),
          description: this.$t('onboarding_email_description'),
        },
        PHONE: {
          component: 'PhoneInputStep',
          title: this.$t('onboarding_phone_title'),
          description: this.$t('onboarding_phone_description'),
        },
        HOUSEHOLD_MEMBERS: {
          component: 'HouseholdMembersStep',
          title: this.$t('onboarding_household_title'),
          description: this.$t('onboarding_household_description'),
        },
        GIVE_BACK: {
          component: 'GiveBackStep',
          title: this.$t('onboarding_give_back_title'),
          description: this.$t('onboarding_give_back_description'),
        },
        CONGRATULATIONS: {
          component: 'CongratulationsStep',
          title: '',
        },
      },
      isOpen: true,
      currentStep: steps.DEMOGRAPHIC,
      addresses: [],
      stepsValues: [],
      saving: false,
    }
  },
  computed: {
    ...mapState('fiiAuth', ['identity']),
    currentStepIndex() {
      return this.stepsValues.indexOf(this.currentStep)
    },
    firstStep() {
      return this.stepsValues[0]
    },
    currentStepComponent() {
      return this.stepsMeta[this.currentStep]?.component ?? 'div'
    },
    stepTitle() {
      return this.stepsMeta[this.currentStep]?.title ?? ''
    },
    stepDescription() {
      return this.stepsMeta[this.currentStep]?.description ?? ''
    },
    // We use a computed property because it will be set before we start the mounting phase where we will skip the first step if necessary.
    hasDemographicData() {
      const { firstName, lastName, dob } = this.identity.meta
      return firstName && lastName && dob
    },
  },
  mounted() {
    if (this.hasDemographicData) {
      this.stepsValues = Object.values(steps).filter(
        (item) => item !== steps.DEMOGRAPHIC
      )
      this.currentStep = this.stepsValues[0]
    } else {
      this.stepsValues = Object.values(steps)
    }
  },
  methods: {
    savingStep() {
      this.saving = true
    },
    stoppedSaving() {
      this.saving = false
    },
    nextStep() {
      this.stoppedSaving()
      this.currentStep = this.stepsValues[this.currentStepIndex + 1]
    },
    prevStep() {
      const isFirstStep = this.currentStep === this.stepsValues[0]

      if (isFirstStep) return

      this.currentStep = this.stepsValues[this.currentStepIndex - 1]
    },
    refreshToken() {
      this.$store.dispatch('fiiAuth/refreshToken')
    },
    closeModal() {
      this.isOpen = false
      this.$emit('close')
      this.stoppedSaving()
      this.refreshToken()
    },
    async closeOnboarding() {
      await this.$axios.patch('/AUTH_URL/user', {
        onboarded: true,
      })
      this.closeModal()
    },
  },
}
</script>

<style scoped></style>
